import { useMemo, useState, type FormEvent } from 'react';
import { Roles } from '@touristation/shared';
import { useAuth } from '../auth/AuthProvider';
import {
  useCompanyEmails, useCreateCompanyEmail, useDeleteCompanyEmail,
  useDepartments, useEmployees, useUpdateCompanyEmail,
} from '../api/hooks';
import { useT } from '../i18n/LangProvider';
import { Query } from '../ui/atoms';
import { useConfirm } from '../ui/confirm';
import { Icon, type IconName } from '../ui/Icon';
import type { CompanyEmail } from '../api/types';
import {
  CC_GENERAL_MANAGEMENT, COMPANY_INTRO, DIRECTORY, OFFICES, OPERATING_GUIDELINES,
} from '../data/companyDirectory';

const EMPTY = { section: '', name: '', email: '', role: '', phone: '', notes: '' };
type Draft = typeof EMPTY;
const NEW_SECTION = '__new__';
type Tab = 'directory' | 'members' | 'contacts';

const mailto = (e: string) => `mailto:${e}`;

/** Copy that works even where the async Clipboard API is blocked (falls back to execCommand). */
async function copyText(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    return;
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); } finally { document.body.removeChild(ta); }
  }
}

/** Small copy-to-clipboard button with a transient "copied" state. */
function CopyBtn({ text, label }: { text: string; label?: string }) {
  const t = useT();
  const [done, setDone] = useState(false);
  if (!text) return null;
  return (
    <button className="btn btn--sm" title={`${t('Copia')}: ${text.slice(0, 60)}${text.length > 60 ? '…' : ''}`}
      onClick={(e) => { e.stopPropagation(); void copyText(text); setDone(true); setTimeout(() => setDone(false), 1300); }}>
      <Icon name={done ? 'check' : 'copy'} size={12} /> {label ?? (done ? t('Copiato') : t('Copia'))}
    </button>
  );
}

/** Join a set of emails as a comma-separated recipient list (deduped). */
const emailList = (emails: (string | null | undefined)[]) =>
  [...new Set(emails.filter(Boolean) as string[])].join(', ');

// ── main: company directory (from the official email) ──────────────────────

function DirectoryView() {
  const t = useT();
  return (
    <div className="col" style={{ gap: 20 }}>
      <div className="card emails-intro" style={{ padding: 18 }}>
        <p className="emails-intro__scope" style={{ margin: '0 0 10px', fontSize: 13.5 }}>{COMPANY_INTRO.scope}</p>
        <p className="muted emails-intro__company" style={{ margin: 0, fontSize: 13 }}>{COMPANY_INTRO.company}</p>
        <div style={{ marginTop: 12, padding: '9px 12px', background: 'var(--t-orange-soft)', borderRadius: 8, fontSize: 12.5 }}>
          <Icon name="info" size={13} /> {t('La Direzione Generale')} (<a href={mailto(CC_GENERAL_MANAGEMENT)}>{CC_GENERAL_MANAGEMENT}</a>) {t('va inserita in copia nelle comunicazioni rilevanti.')}
        </div>
      </div>

      <div>
        <h2 className="h2" style={{ fontSize: 15, marginBottom: 10 }}>{t('Punti vendita e sedi')}</h2>
        <div className="grid grid-2" style={{ gap: 10 }}>
          {OFFICES.map((o) => (
            <div key={o.name} className="card" style={{ padding: 14 }}>
              <div style={{ fontWeight: 600 }}>{o.name}</div>
              <div className="muted" style={{ fontSize: 12.5, marginBottom: 6 }}>{o.address}</div>
              {o.email && <div className="row" style={{ fontSize: 13, gap: 6, alignItems: 'center' }}><Icon name="mail" size={12} /> <a href={mailto(o.email)}>{o.email}</a><button className="icon-btn" title="Copia email" onClick={() => void copyText(o.email!)} style={{ width: 22, height: 22 }}><Icon name="copy" size={11} /></button></div>}
              {o.mobile && <div className="muted" style={{ fontSize: 12.5 }}>📱 {o.mobile}</div>}
              {o.phone && <div className="muted" style={{ fontSize: 12.5 }}>☎ {o.phone}</div>}
            </div>
          ))}
        </div>
      </div>

      {DIRECTORY.map((sec) => (
        <div key={sec.title}>
          <h2 className="h2" style={{ fontSize: 15, marginBottom: 10 }}>
            <Icon name={sec.icon as IconName} size={14} /> {sec.title}
          </h2>
          <div className="grid grid-2" style={{ gap: 10 }}>
            {sec.roles.map((r) => (
              <div key={r.role + (r.person ?? '')} className="card" style={{ padding: 14 }}>
                <div className="spaced" style={{ alignItems: 'flex-start' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{r.role}</div>
                    {r.person && <div className="muted" style={{ fontSize: 12.5 }}>{r.person}</div>}
                  </div>
                  {r.ccGm && <span className="lrn-chips__n" title="Direzione Generale sempre in copia">cc GM</span>}
                </div>
                {r.email && <div className="row" style={{ fontSize: 13, marginTop: 4, gap: 6, alignItems: 'center' }}><Icon name="mail" size={12} /> <a href={mailto(r.email)}>{r.email}</a><button className="icon-btn" title="Copia email" onClick={() => void copyText(r.email!)} style={{ width: 22, height: 22 }}><Icon name="copy" size={11} /></button></div>}
                {r.competencies && (
                  <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12.5, color: 'var(--ink-2)' }}>
                    {r.competencies.map((c) => <li key={c} style={{ marginBottom: 2 }}>{c}</li>)}
                  </ul>
                )}
                {r.note && <div className="muted" style={{ fontSize: 12, marginTop: 8, fontStyle: 'italic' }}>{r.note}</div>}
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="card" style={{ padding: 16 }}>
        <h2 className="h2" style={{ fontSize: 14, marginBottom: 8 }}>{t('Indicazioni operative')}</h2>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--ink-2)' }}>
          {OPERATING_GUIDELINES.map((g) => <li key={g} style={{ marginBottom: 3 }}>{g}</li>)}
        </ul>
      </div>
    </div>
  );
}

// ── platform members grouped by department (auto) ──────────────────────────

function MembersView({ search }: { search: string }) {
  const t = useT();
  const employeesQ = useEmployees();
  const deptsQ = useDepartments();

  const employees = (employeesQ.data?.items ?? []).filter((e) => e.active);
  const depts = deptsQ.data ?? [];
  const deptName = (id: string | null) => depts.find((d) => d.id === id)?.name ?? t('Senza reparto');

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return employees;
    return employees.filter((e) => [e.name, e.email, e.role].some((v) => (v ?? '').toLowerCase().includes(t)));
  }, [employees, search]);

  const byDept = useMemo(() => {
    const m: Record<string, typeof employees> = {};
    for (const e of filtered) (m[deptName(e.departmentId)] ??= []).push(e);
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b, 'it'));
  }, [filtered, depts]);

  if (employeesQ.isLoading) return <div className="muted" style={{ padding: 20 }}>{t('Carico…')}</div>;
  if (byDept.length === 0) return <div className="muted" style={{ padding: 20 }}>{t('Nessun membro corrisponde.')}</div>;

  const allEmails = emailList(filtered.map((e) => e.email));

  return (
    <div className="col" style={{ gap: 20 }}>
      <div className="spaced" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div className="muted" style={{ fontSize: 12.5 }}>
          {t('Elenco generato automaticamente da chi è iscritto alla piattaforma, suddiviso per reparto. Usa i pulsanti per copiare gli indirizzi e incollarli nel campo destinatari della mail.')}
        </div>
        <CopyBtn text={allEmails} label={t('Copia tutti ({n})', { n: filtered.length })} />
      </div>
      {byDept.map(([dept, people]) => (
        <div key={dept}>
          <div className="spaced" style={{ marginBottom: 8 }}>
            <h2 className="h2" style={{ fontSize: 15, margin: 0 }}>
              {dept} <span className="muted" style={{ fontWeight: 400, fontSize: 12.5 }}>({people.length})</span>
            </h2>
            <CopyBtn text={emailList(people.map((e) => e.email))} label={t('Copia reparto ({n})', { n: people.length })} />
          </div>
          <div className="grid grid-2" style={{ gap: 10 }}>
            {people.map((e) => (
              <div key={e.id} className="card" style={{ padding: 12, display: 'flex', gap: 10, alignItems: 'center' }}>
                <span className="lrn-avatar is-neutral" style={{ flex: '0 0 auto' }}>{e.initials ?? e.name.slice(0, 2).toUpperCase()}</span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{e.name}</div>
                  {e.role && <div className="muted" style={{ fontSize: 12 }}>{e.role}</div>}
                  <a href={mailto(e.email)} style={{ fontSize: 13 }}>{e.email}</a>
                </div>
                <button className="icon-btn" title="Copia email" onClick={() => void copyText(e.email)}><Icon name="copy" size={13} /></button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── manual contacts (the existing CompanyEmail address book) ───────────────

function ContactForm({ initial, sections, busy, error, submitLabel, onSubmit, onCancel }: {
  initial: Draft; sections: string[]; busy: boolean; error: string | null;
  submitLabel: string; onSubmit: (d: Draft) => void; onCancel: () => void;
}) {
  const t = useT();
  const [form, setForm] = useState<Draft>(initial);
  const [mode, setMode] = useState<string>(() => initial.section && sections.includes(initial.section) ? initial.section : NEW_SECTION);
  const set = (patch: Partial<Draft>) => setForm((f) => ({ ...f, ...patch }));
  const missing = !form.section.trim() || !form.name.trim() || !form.email.trim();

  return (
    <form className="card" style={{ padding: 18, marginBottom: 20 }} onSubmit={(e: FormEvent) => { e.preventDefault(); onSubmit(form); }}>
      {error && <div style={{ background: '#fdecec', border: '1px solid #f3b6b6', color: '#8f2020', borderRadius: 8, padding: '9px 12px', marginBottom: 12, fontSize: 13 }}><Icon name="warn" size={13} /> {error}</div>}
      <div className="row" style={{ gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <label className="field-label">{t('Sezione')}</label>
          <select className="input" value={mode} onChange={(e) => { const v = e.target.value; setMode(v); set({ section: v === NEW_SECTION ? '' : v }); }}>
            {sections.map((s) => <option key={s} value={s}>{s}</option>)}
            <option value={NEW_SECTION}>{t('+ Nuova sezione…')}</option>
          </select>
          {mode === NEW_SECTION && <input className="input" placeholder={t('Nome della nuova sezione')} value={form.section} onChange={(e) => set({ section: e.target.value })} style={{ marginTop: 8 }} autoFocus />}
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <label className="field-label">{t('Nome / casella')}</label>
          <input className="input" placeholder={t('Es. Prenotazioni Roma')} value={form.name} onChange={(e) => set({ name: e.target.value })} />
        </div>
      </div>
      <div className="row" style={{ gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: 2, minWidth: 220 }}>
          <label className="field-label">Email</label>
          <input className="input" placeholder="nome@ticketstation.it" value={form.email} onChange={(e) => set({ email: e.target.value })} />
        </div>
        <div style={{ flex: 1, minWidth: 150 }}>
          <label className="field-label">{t('Ruolo (opz.)')}</label>
          <input className="input" placeholder={t('Responsabile')} value={form.role} onChange={(e) => set({ role: e.target.value })} />
        </div>
        <div style={{ flex: 1, minWidth: 150 }}>
          <label className="field-label">{t('Telefono (opz.)')}</label>
          <input className="input" placeholder="+39 …" value={form.phone} onChange={(e) => set({ phone: e.target.value })} />
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label className="field-label">{t('Note (opz.)')}</label>
        <input className="input" placeholder={t('Quando usarla, chi la presidia…')} value={form.notes} onChange={(e) => set({ notes: e.target.value })} />
      </div>
      <div className="cluster" style={{ justifyContent: 'space-between' }}>
        <span className="muted" style={{ fontSize: 12 }}>{missing ? t('Sezione, nome ed email sono obbligatori.') : ' '}</span>
        <span className="cluster">
          <button type="button" className="btn btn--sm" onClick={onCancel}>{t('Annulla')}</button>
          <button type="submit" className="btn btn--sm btn--brand" disabled={busy || missing}>{busy ? t('Salvataggio…') : submitLabel}</button>
        </span>
      </div>
    </form>
  );
}

function ContactsView({ canManage }: { canManage: boolean }) {
  const t = useT();
  const confirm = useConfirm();
  const q = useCompanyEmails();
  const create = useCreateCompanyEmail();
  const update = useUpdateCompanyEmail();
  const remove = useDeleteCompanyEmail();

  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<CompanyEmail | null>(null);
  const [error, setError] = useState<string | null>(null);

  const items = q.data?.items ?? [];
  const sections = useMemo(() => [...new Set(items.map((c) => c.section))].sort((a, b) => a.localeCompare(b, 'it')), [items]);

  const payload = (d: Draft) => ({ section: d.section.trim(), name: d.name.trim(), email: d.email.trim(), role: d.role.trim() || undefined, phone: d.phone.trim() || undefined, notes: d.notes.trim() || undefined });

  async function submitNew(d: Draft) { setError(null); try { await create.mutateAsync(payload(d)); setShowNew(false); } catch (e) { setError(e instanceof Error ? e.message : t('Creazione non riuscita.')); } }
  async function submitEdit(d: Draft) { if (!editing) return; setError(null); try { await update.mutateAsync({ id: editing.id, ...payload(d) }); setEditing(null); } catch (e) { setError(e instanceof Error ? e.message : t('Modifica non riuscita.')); } }
  async function del(c: CompanyEmail) { if (!(await confirm({ message: t('Eliminare il contatto “{name}”?', { name: c.name }), confirmLabel: t('Elimina'), danger: true }))) return; try { await remove.mutateAsync(c.id); } catch (e) { setError(e instanceof Error ? e.message : t('Eliminazione non riuscita.')); } }

  return (
    <div>
      {canManage && !showNew && !editing && (
        <button className="btn btn--brand btn--sm" style={{ marginBottom: 14 }} onClick={() => { setError(null); setShowNew(true); }}>
          <Icon name="plus" size={13} /> {t('Nuovo contatto')}
        </button>
      )}
      {error && !showNew && !editing && <div style={{ background: '#fdecec', border: '1px solid #f3b6b6', color: '#8f2020', borderRadius: 8, padding: '9px 12px', marginBottom: 14, fontSize: 13 }}><Icon name="warn" size={13} /> {error}</div>}
      {showNew && canManage && <ContactForm key="new" initial={{ ...EMPTY, section: sections[0] ?? '' }} sections={sections} busy={create.isPending} error={error} submitLabel={t('Aggiungi contatto')} onSubmit={(d) => void submitNew(d)} onCancel={() => { setShowNew(false); setError(null); }} />}
      {editing && canManage && <ContactForm key={editing.id} initial={{ section: editing.section, name: editing.name, email: editing.email, role: editing.role ?? '', phone: editing.phone ?? '', notes: editing.notes ?? '' }} sections={sections} busy={update.isPending} error={error} submitLabel={t('Salva modifiche')} onSubmit={(d) => void submitEdit(d)} onCancel={() => { setEditing(null); setError(null); }} />}

      <Query query={q} empty={{ test: () => items.length === 0, title: t('Rubrica vuota'), hint: canManage ? t('Aggiungi il primo contatto.') : t('I contatti aziendali vengono gestiti dagli amministratori.') }}>
        {() => {
          const bySection = items.reduce<Record<string, CompanyEmail[]>>((acc, c) => { (acc[c.section] ??= []).push(c); return acc; }, {});
          return (
            <div className="col" style={{ gap: 20 }}>
              {Object.entries(bySection).sort(([a], [b]) => a.localeCompare(b, 'it')).map(([section, contacts]) => (
                <div key={section}>
                  <h2 className="h2" style={{ fontSize: 15, marginBottom: 8 }}>{section} <span className="muted" style={{ fontWeight: 400, fontSize: 12.5 }}>({contacts.length})</span></h2>
                  <div className="grid grid-2" style={{ gap: 10 }}>
                    {contacts.map((c) => (
                      <div key={c.id} className="card" style={{ padding: 14 }}>
                        <div className="spaced" style={{ alignItems: 'flex-start' }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600 }}>{c.name}</div>
                            {c.role && <div className="muted" style={{ fontSize: 12 }}>{c.role}</div>}
                            <a href={mailto(c.email)} style={{ fontSize: 13 }}>{c.email}</a>
                            {c.phone && <div className="muted" style={{ fontSize: 12 }}>{c.phone}</div>}
                            {c.notes && <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{c.notes}</div>}
                          </div>
                          {canManage && (
                            <div className="row" style={{ gap: 4, flexShrink: 0 }}>
                              <button className="icon-btn" title={t('Modifica')} onClick={() => { setShowNew(false); setError(null); setEditing(c); }}><Icon name="edit" size={13} /></button>
                              <button className="icon-btn icon-btn--danger" title={t('Elimina')} onClick={() => void del(c)}><Icon name="trash" size={13} /></button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          );
        }}
      </Query>
    </div>
  );
}

export function EmailsScreen() {
  const t = useT();
  const auth = useAuth();
  const canManage = auth.hasRole(Roles.ADMIN) || auth.hasRole(Roles.TEAM_LEAD);
  const [tab, setTab] = useState<Tab>('directory');
  const [search, setSearch] = useState('');

  const tabs: { id: Tab; label: string; icon: string }[] = [
    { id: 'directory', label: 'Rubrica aziendale', icon: 'book' },
    { id: 'members', label: 'Membri per reparto', icon: 'users' },
    { id: 'contacts', label: 'Contatti', icon: 'at' },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div className="page-head" style={{ marginBottom: 16 }}>
        <div>
          <h1 className="h1">{t('Mail aziendali')}</h1>
          <div className="page-head__sub">{t('Riferimenti aziendali, rubrica del personale e contatti.')}</div>
        </div>
        {tab === 'members' && (
          <input className="input" placeholder={t('Cerca persona…')} value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 220 }} />
        )}
      </div>

      <div className="lrn-tabs" style={{ marginBottom: 20 }}>
        {tabs.map((tb) => (
          <button key={tb.id} className={`lrn-tab ${tab === tb.id ? 'is-active' : ''}`} onClick={() => setTab(tb.id)}>
            <Icon name={tb.icon as IconName} size={14} /> {t(tb.label)}
          </button>
        ))}
      </div>

      {tab === 'directory' && <DirectoryView />}
      {tab === 'members' && <MembersView search={search} />}
      {tab === 'contacts' && <ContactsView canManage={canManage} />}
    </div>
  );
}
